// src/components/superadmin/ClientList.jsx
// #Revised UI_v2 — SE2: search bar, column sort, status/module filters, health dot, CSV export
// #Revised UI_v2 — SE3: expandable drill-down row (module chips, customer count, bookings, PayNow)
// #Revised UI_v2 — SE6: "View Portal" quick-link opens salon admin in new tab
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getCountFromServer } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { ICON_BASE_URL } from '../../utils/moduleIcons';

// #Revised UI_v2 — SE3: module display helpers
const MODULE_ICONS = {
  home:           `${ICON_BASE_URL}core-module.svg?alt=media&token=6a7e6302-93f8-4b42-9495-c9c26f3296da`,
  promotions:     `${ICON_BASE_URL}loyalty-management.svg?alt=media&token=1f626298-fb6c-4f59-a2b1-5b7c9fa404cd`,
  communications: `${ICON_BASE_URL}communication-management.svg?alt=media&token=e8485fc2-cf71-4d76-ab72-6e804212c6b1`,
  bookings:       `${ICON_BASE_URL}booking-management.svg?alt=media&token=248a2ac6-7a00-4990-82b5-b354ede53ec7`,
  reports:        `${ICON_BASE_URL}reports-management.svg?alt=media&token=bcfef9fc-6076-42f5-ad60-f633cc51af59`,
  bespokeai:      `${ICON_BASE_URL}bespoke-ai.svg?alt=media&token=f0b355ff-a133-4ded-a36a-dac8bd0061c5`,
};
const MODULE_LABELS = {
  home:           'Home',
  promotions:     'Promotions',
  communications: 'Comms',
  bookings:       'Bookings',
  reports:        'Reports',
  bespokeai:      'AI',
};

function ClientList({ clients, onClientUpdated, onCreateClient }) {
  const navigate = useNavigate();

  // #Revised UI_v2 — SE2: search + filter + sort state
  const [search,       setSearch]       = useState('');
  const [sortKey,      setSortKey]      = useState('name');
  const [sortDir,      setSortDir]      = useState('asc');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterModule, setFilterModule] = useState('');

  // #Revised UI_v2 — SE3: expandable row state + fetched detail data
  const [expandedId,      setExpandedId]      = useState(null);
  const [drilldownData,   setDrilldownData]   = useState({});
  const [drilldownLoading,setDrilldownLoading]= useState(false);

  const getSalonId    = (c) => c.salonId || c.salon?.salonId || 'Not configured';
  const getClientStatus = (c) => c.status || 'active';

  // #Revised UI_v2 — SE2: health indicator dot
  const getHealth = (client) => {
    if (getClientStatus(client) === 'inactive') return 'red';
    if (client.updatedAt) {
      const updated   = client.updatedAt?.seconds
        ? new Date(client.updatedAt.seconds * 1000)
        : new Date(client.updatedAt);
      const daysSince = (Date.now() - updated.getTime()) / 86400000;
      if (daysSince > 30) return 'amber';
    }
    return 'green';
  };

  // #Revised UI_v2 — SE2: sortable columns
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };
  const sortArrow = (key) => {
    if (sortKey !== key) return ' ↕';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  // #Revised UI_v2 — SE2: filtered + sorted list
  const sortedFiltered = clients
    .filter(c => {
      const name = (c.name || '').toLowerCase();
      const sid  = getSalonId(c).toLowerCase();
      const q    = search.toLowerCase();
      if (q && !name.includes(q) && !sid.includes(q)) return false;
      if (filterStatus && getClientStatus(c) !== filterStatus)  return false;
      if (filterModule && !c.enabledModules?.[filterModule])    return false;
      return true;
    })
    .sort((a, b) => {
      let av, bv;
      if (sortKey === 'name') {
        av = (a.name || '').toLowerCase();
        bv = (b.name || '').toLowerCase();
      } else if (sortKey === 'createdAt') {
        av = a.createdAt?.seconds ?? 0;
        bv = b.createdAt?.seconds ?? 0;
      } else if (sortKey === 'status') {
        av = getClientStatus(a);
        bv = getClientStatus(b);
      } else {
        av = ''; bv = '';
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });

  // #Revised UI_v2 — SE2: CSV export
  const handleExportCSV = () => {
    const headers = ['Client Name', 'Salon ID', 'Status', 'Modules', 'Created'];
    const rows    = sortedFiltered.map(c => [
      c.name || '',
      getSalonId(c),
      getClientStatus(c),
      ['Home',
        c.enabledModules?.promotions     ? 'Promotions'     : '',
        c.enabledModules?.communications ? 'Communications' : '',
        c.enabledModules?.bookings       ? 'Bookings'       : '',
        c.enabledModules?.reports        ? 'Reports'        : '',
        c.enabledModules?.bespokeai      ? 'BespokeAI'      : '',
      ].filter(Boolean).join(' | '),
      c.createdAt?.seconds
        ? new Date(c.createdAt.seconds * 1000).toLocaleDateString('en-SG')
        : '',
    ]);
    const csv  = [headers, ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `bespoke-clients-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // #Revised UI_v2 — SE3: fetch drill-down stats on expand
  const fetchDrilldown = useCallback(async (client) => {
    const salonId = getSalonId(client);
    if (!salonId || salonId === 'Not configured') return;
    if (drilldownData[client.id]) return; // cached

    setDrilldownLoading(true);
    try {
      const [custSnap, bookSnap] = await Promise.all([
        getCountFromServer(
          query(collection(db, 'customers'), where('salonId', '==', salonId))
        ),
        getCountFromServer(
          query(collection(db, 'appointments'), where('salonId', '==', salonId))
        ),
      ]);

      setDrilldownData(prev => ({
        ...prev,
        [client.id]: {
          customerCount:  custSnap.data().count,
          bookingsTotal:  bookSnap.data().count,
        },
      }));
    } catch (err) {
      console.error('Drilldown fetch failed for', salonId, err);
      setDrilldownData(prev => ({
        ...prev,
        [client.id]: { customerCount: '—', bookingsTotal: '—' },
      }));
    } finally {
      setDrilldownLoading(false);
    }
  }, [drilldownData]);

  const toggleExpand = (client) => {
    if (expandedId === client.id) {
      setExpandedId(null);
    } else {
      setExpandedId(client.id);
      fetchDrilldown(client);
    }
  };

  // #Revised UI_v2 — SE6: open salon admin portal in new tab
  const handleViewAsClient = (e, client) => {
    e.stopPropagation();
    const salonId = getSalonId(client);
    if (salonId && salonId !== 'Not configured') {
      window.open(`/BespokeBeauty/${salonId}/admin`, '_blank');
    }
  };

  const handleConfigureClient = (e, clientId) => {
    e.stopPropagation();
    navigate(`/superadmin/client-config/${clientId}`);
  };

  return (
    <div className="client-list-table-container">

      {/* ── Action bar ── */}
      <div className="client-list-actions-bar">
        {onCreateClient && (
          <button className="create-client-button" onClick={onCreateClient}>
            + Create New Client
          </button>
        )}
        {/* #Revised UI_v2 — SE2: CSV export */}
        <button className="sa-export-btn" onClick={handleExportCSV}>
          ↓ Export CSV
        </button>
      </div>

      {/* #Revised UI_v2 — SE2: search + filter bar */}
      <div className="sa-client-filter-bar">
        <input
          className="sa-client-search"
          type="text"
          placeholder="Search by name or salon ID…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className="sa-client-filter-select"
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select
          className="sa-client-filter-select"
          value={filterModule}
          onChange={e => setFilterModule(e.target.value)}
        >
          <option value="">All Modules</option>
          <option value="promotions">Promotions</option>
          <option value="communications">Communications</option>
          <option value="bookings">Bookings</option>
          <option value="reports">Reports</option>
          <option value="bespokeai">BespokeAI</option>
        </select>
        <span className="sa-client-count">
          {sortedFiltered.length} of {clients.length} clients
        </span>
      </div>

      {/* ── Main table ── */}
      <table className="client-list-table">
        <thead>
          <tr>
            {/* health dot column */}
            <th style={{ width: 28 }} />
            <th className="sortable" onClick={() => handleSort('name')}>
              Client Name{sortArrow('name')}
            </th>
            <th>Salon ID</th>
            <th className="sortable" onClick={() => handleSort('status')}>
              Status{sortArrow('status')}
            </th>
            <th>Modules</th>
            <th className="sortable" onClick={() => handleSort('createdAt')}>
              Created{sortArrow('createdAt')}
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedFiltered.map(client => {
            const isExpanded = expandedId === client.id;
            const health     = getHealth(client);
            const status     = getClientStatus(client);
            const dd         = drilldownData[client.id];

            const enabledModules = [
              'home',
              ...(client.enabledModules?.promotions     ? ['promotions']     : []),
              ...(client.enabledModules?.communications ? ['communications'] : []),
              ...(client.enabledModules?.bookings       ? ['bookings']       : []),
              ...(client.enabledModules?.reports        ? ['reports']        : []),
              ...(client.enabledModules?.bespokeai      ? ['bespokeai']      : []),
            ];

            return (
              <React.Fragment key={client.id}>
                <tr
                  className={`client-row ${isExpanded ? 'expanded' : ''}`}
                  onClick={() => toggleExpand(client)}
                  style={{ cursor: 'pointer' }}
                >
                  {/* #Revised UI_v2 — SE2: health indicator dot */}
                  <td>
                    <span
                      className={`sa-health-dot sa-health-dot--${health}`}
                      title={
                        health === 'green' ? 'Active — recent activity' :
                        health === 'amber' ? 'Active — no recent activity' :
                                             'Inactive'
                      }
                    />
                  </td>
                  <td className="client-name-cell">
                    <strong>{client.name}</strong>
                  </td>
                  <td className="salon-id-cell">{getSalonId(client)}</td>
                  <td className="status-cell">
                    <span className={`status-badge-table ${status}`}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </span>
                  </td>
                  {/* #Revised UI_v2 — SE3: module icon chips */}
                  <td className="modules-cell">
                    <div className="sa-module-chips">
                      {enabledModules.map(m => (
                        <span key={m} className="sa-module-chip" title={MODULE_LABELS[m]}>
                          <img src={MODULE_ICONS[m]} alt={MODULE_LABELS[m]} className="sa-module-chip-icon" />
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>
                    {client.createdAt?.seconds
                      ? new Date(client.createdAt.seconds * 1000).toLocaleDateString('en-SG', {
                          day: '2-digit', month: 'short', year: 'numeric',
                        })
                      : '—'}
                  </td>
                  <td className="actions-cell" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={(e) => handleConfigureClient(e, client.id)}
                      className="configure-button"
                    >
                      Configure
                    </button>
                    {/* #Revised UI_v2 — SE6: "View Portal" quick-link */}
                    <button
                      onClick={(e) => handleViewAsClient(e, client)}
                      className="sa-view-client-btn"
                      title="Open this salon's admin portal in a new tab"
                    >
                      View Portal ↗
                    </button>
                  </td>
                </tr>

                {/* #Revised UI_v2 — SE3: drill-down detail row */}
                {isExpanded && (
                  <tr className="sa-drilldown-row">
                    <td colSpan={7}>
                      <div className="sa-drilldown-panel">
                        {drilldownLoading && !dd && (
                          <span className="sa-drilldown-loading">Loading details…</span>
                        )}

                        {dd && (
                          <div className="sa-drilldown-stats">
                            <div className="sa-drilldown-stat">
                              <span className="sa-drilldown-stat-value">{dd.customerCount}</span>
                              <span className="sa-drilldown-stat-key">Customers</span>
                            </div>
                            <div className="sa-drilldown-stat">
                              <span className="sa-drilldown-stat-value">{dd.bookingsTotal}</span>
                              <span className="sa-drilldown-stat-key">Total Bookings</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {sortedFiltered.length === 0 && (
        <div className="empty-state">
          <p>
            {clients.length === 0
              ? 'No clients found. Create your first client to get started.'
              : 'No clients match the current search or filters.'}
          </p>
        </div>
      )}
    </div>
  );
}

export default ClientList;
